/**
 * Sentry Enrichment Service
 *
 * Maintains persistent Sentry contexts so every error report automatically
 * carries the current workspace, kernel, and stream state without any
 * per-call-site work. Call the update functions whenever state changes;
 * Sentry attaches the latest snapshot to all subsequent events.
 */

import * as Sentry from '@sentry/react';
import type { KernelHealth } from '@/lib/jupyter/JupyterClient';

export interface WorkspaceContextData {
  id: string;
  name: string;
  state: string;
  version?: string;
  /** Unix timestamp (seconds) when the workspace was created */
  createdAt?: number;
  /** Unix timestamp (seconds) when the current session started running */
  runningAt?: number;
}

export interface StreamContextData {
  connected: boolean;
  retryCount: number;
  phase?: string;
  connectionType?: 'local' | 'remote';
  iceConnectionState?: string;
  iceGatheringState?: string;
}

export interface SimulationContextData {
  sessionId: string | null;
  state: string;
  fps: number | null;
  uptimeS: number | null;
  kernelManaged?: boolean | null;
}

// NOTE: uptimeSeconds here is the workspace session uptime derived from
// Rome's runningAt timestamp, NOT the simulation process uptime. The sim
// process uptime comes from /sim/status (see updateSimulationContext).
export function updateWorkspaceContext(workspace: WorkspaceContextData | null): void {
  if (workspace) {
    const nowSec = Math.floor(Date.now() / 1000);
    const uptimeSec = workspace.runningAt ? nowSec - workspace.runningAt : null;
    Sentry.setContext('workspace', {
      id: workspace.id,
      name: workspace.name,
      state: workspace.state,
      version: workspace.version ?? null,
      createdAt: workspace.createdAt ? new Date(workspace.createdAt * 1000).toISOString() : null,
      runningAt: workspace.runningAt ? new Date(workspace.runningAt * 1000).toISOString() : null,
      uptimeSeconds: uptimeSec,
    });
    Sentry.setTag('workspace_id', workspace.id);
    Sentry.setTag('workspace_state', workspace.state);
    if (workspace.version) {
      Sentry.setTag('workspace_version', workspace.version);
    }
  } else {
    Sentry.setContext('workspace', null);
    Sentry.setTag('workspace_id', undefined);
    Sentry.setTag('workspace_state', undefined);
    Sentry.setTag('workspace_version', undefined);
  }
}

export function updateKernelContext(health: KernelHealth): void {
  Sentry.setContext('kernel', {
    status: health.status,
    connectionStatus: health.connectionStatus,
    isHealthy: health.isHealthy,
  });
  Sentry.setTag('kernel_status', health.status);
}

export function updateStreamContext(state: StreamContextData): void {
  Sentry.setContext('stream', {
    connected: state.connected,
    retryCount: state.retryCount,
    phase: state.phase ?? null,
    connectionType: state.connectionType ?? null,
    iceConnectionState: state.iceConnectionState ?? null,
    iceGatheringState: state.iceGatheringState ?? null,
  });
  Sentry.setTag('stream_connected', String(state.connected));
  Sentry.setTag('stream_phase', state.phase);
}

export function updateSimulationContext(sim: SimulationContextData | null): void {
  if (sim) {
    Sentry.setContext('simulation', {
      sessionId: sim.sessionId,
      state: sim.state,
      fps: sim.fps,
      uptimeS: sim.uptimeS,
      kernelManaged: sim.kernelManaged ?? null,
    });
    Sentry.setTag('sim_state', sim.state);
    Sentry.setTag('sim_session_id', sim.sessionId ?? undefined);
    Sentry.setTag('sim_mode', sim.kernelManaged != null ? (sim.kernelManaged ? 'kernel' : 'standalone') : undefined);
  } else {
    Sentry.setContext('simulation', null);
    Sentry.setTag('sim_state', undefined);
    Sentry.setTag('sim_session_id', undefined);
    Sentry.setTag('sim_mode', undefined);
  }
}

export function updateReconnectionContext(reconnectCount: number, disconnectedSinceMs: number | null): void {
  if (reconnectCount > 0) {
    Sentry.setContext('reconnection', {
      attemptCount: reconnectCount,
      disconnectedSinceMs,
    });
  } else {
    Sentry.setContext('reconnection', null);
  }
}

export function clearAllContexts(): void {
  Sentry.setContext('workspace', null);
  Sentry.setContext('kernel', null);
  Sentry.setContext('stream', null);
  Sentry.setContext('simulation', null);
  Sentry.setContext('reconnection', null);
  Sentry.setTag('workspace_id', undefined);
  Sentry.setTag('workspace_state', undefined);
  Sentry.setTag('workspace_version', undefined);
  Sentry.setTag('kernel_status', undefined);
  Sentry.setTag('stream_connected', undefined);
  Sentry.setTag('stream_phase', undefined);
  Sentry.setTag('sim_state', undefined);
  Sentry.setTag('sim_session_id', undefined);
  Sentry.setTag('sim_mode', undefined);
}
